/** @file Trails layer: the tip path of chosen bones across every animation frame, a dot per frame. */
import type { Sink } from '../draw/createSink';
import { emitDisc } from '../draw/emitDisc';
import { emitLine } from '../draw/emitLine';
import { fillSegment } from '../draw/fillSegment';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';

const seg = new Float64Array(4);
const line = new Float64Array(4);

/** One bone's polyline through `input.trailPoses`; the first frame gets a bigger dot. */
function drawTrail(sink: Sink, input: OverlayInput, bone: number, color: number): void {
  const length = input.data.bones[bone]?.length ?? 0;
  let px = NaN, py = NaN;
  (input.trailPoses ?? []).forEach((pose, f) => {
    boneSegment(pose, input.camera, bone, length, seg);
    const x = seg[2] ?? NaN, y = seg[3] ?? NaN;
    if (f > 0) emitLine(sink, fillSegment(line, px, py, x, y), OVERLAY_STYLE.lineWidth, color);
    emitDisc(sink, x, y, f === 0 ? 3.5 : 2, color);
    px = x;
    py = y;
  });
}

/** `options.trails`: bone names (unknown names draw nothing; parseOverlays reports them), one colour each. */
export function drawTrails(sink: Sink, input: OverlayInput): void {
  const names = input.options.trails;
  if (names === undefined) return;
  names.forEach((name, k) => {
    const bone = input.data.boneIndex.get(name);
    if (bone !== undefined) drawTrail(sink, input, bone, OVERLAY_STYLE.trail[k % OVERLAY_STYLE.trail.length] ?? OVERLAY_STYLE.joint);
  });
}
